import * as MuiIcons from '@mui/icons-material';

/**
 * Get MUI Icon component by name
 * @param {string} iconName - Icon identifier (e.g., 'business', 'local_hospital')
 * @param {string} variant - Icon variant: 'filled', 'outlined', 'rounded', 'two_tone', 'sharp' (default: 'filled')
 * @returns {React.Component} - MUI Icon component
 */
export const getIcon = (iconName, variant = 'filled') => {
    if (!iconName) return MuiIcons.Business; // Default icon
    
    // Convert snake_case to PascalCase (e.g., 'local_hospital' -> 'LocalHospital')
    const pascalCase = iconName
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join('');
    
    // Add variant suffix
    let iconComponentName = pascalCase;
    if (variant === 'outlined') {
        iconComponentName = `${pascalCase}Outlined`;
    } else if (variant === 'rounded') {
        iconComponentName = `${pascalCase}Rounded`;
    } else if (variant === 'two_tone') {
        iconComponentName = `${pascalCase}TwoTone`;
    } else if (variant === 'sharp') {
        iconComponentName = `${pascalCase}Sharp`;
    }
    
    // Return the icon component or default to Business icon
    return MuiIcons[iconComponentName] || MuiIcons.Business;
};

/**
 * Available department icons with categories
 */
export const departmentIcons = [
    // Core Departments
    { name: 'business', label: 'Business', category: 'Core' },
    { name: 'groups', label: 'Human Resources', category: 'Core' },
    { name: 'account_balance', label: 'Finance', category: 'Core' },
    { name: 'computer', label: 'IT', category: 'Core' },
    { name: 'engineering', label: 'Engineering', category: 'Core' },
    { name: 'handshake', label: 'Sales', category: 'Core' },
    { name: 'campaign', label: 'Marketing', category: 'Core' },
    { name: 'support', label: 'Support', category: 'Core' },
    
    // Operations
    { name: 'check_circle', label: 'Operations', category: 'Operations' },
    { name: 'factory', label: 'Manufacturing', category: 'Operations' },
    { name: 'warehouse', label: 'Warehouse', category: 'Operations' },
    { name: 'local_shipping', label: 'Logistics', category: 'Operations' },
    { name: 'inventory', label: 'Inventory', category: 'Operations' },
    { name: 'precision_manufacturing', label: 'Production', category: 'Operations' },
    { name: 'qr_code_scanner', label: 'Quality Control', category: 'Operations' },
    
    // Services
    { name: 'cleaning_services', label: 'Housekeeping', category: 'Services' },
    { name: 'security', label: 'Security', category: 'Services' },
    { name: 'local_hospital', label: 'Medical', category: 'Services' },
    { name: 'restaurant', label: 'Food Service', category: 'Services' },
    { name: 'health_and_safety', label: 'Health & Safety', category: 'Services' },
    { name: 'plumbing', label: 'Maintenance', category: 'Services' },
    
    // Management
    { name: 'admin_panel_settings', label: 'Administration', category: 'Management' },
    { name: 'gavel', label: 'Legal', category: 'Management' },
    { name: 'assessment', label: 'Compliance', category: 'Management' },
    { name: 'card_travel', label: 'Procurement', category: 'Management' },
    { name: 'schema', label: 'Planning', category: 'Management' },
    
    // Customer-Facing
    { name: 'store', label: 'Retail', category: 'Customer' },
    { name: 'headset', label: 'Customer Service', category: 'Customer' },
    { name: 'person_search', label: 'Reception', category: 'Customer' },
    { name: 'room_service', label: 'Guest Services', category: 'Customer' },
    
    // Technical
    { name: 'science', label: 'Research', category: 'Technical' },
    { name: 'biotech', label: 'Laboratory', category: 'Technical' },
    { name: 'settings', label: 'Technical Services', category: 'Technical' },
    { name: 'developer_mode', label: 'Development', category: 'Technical' },
];

/**
 * Get icons by category
 * @param {string} category - Category name
 * @returns {Array} - Filtered icons
 */
export const getIconsByCategory = (category) => {
    return departmentIcons.filter(icon => icon.category === category);
};

/**
 * Get all categories
 * @returns {Array} - Unique categories
 */
export const getCategories = () => {
    return [...new Set(departmentIcons.map(icon => icon.category))];
};

/**
 * Search icons by name or label
 * @param {string} query - Search query
 * @returns {Array} - Filtered icons
 */
export const searchIcons = (query) => {
    if (!query) return departmentIcons;
    
    const lowerQuery = query.toLowerCase();
    return departmentIcons.filter(icon => 
        icon.name.toLowerCase().includes(lowerQuery) || 
        icon.label.toLowerCase().includes(lowerQuery)
    );
};

